import Sidebar from '@/components/layout/sidebar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/hooks/useAuth';
import { apiRequest } from '@/lib/queryClient';
import { useQuery } from '@tanstack/react-query';
import { jsPDF } from 'jspdf';
import {
  Calendar,
  Download,
  FileText,
  PanelLeftClose,
  PanelLeftOpen,
  RefreshCw,
  Target,
} from 'lucide-react';
import { useEffect, useState } from 'react';

interface TaskAnalysisData {
  date: string;
  totalTasks: number;
  notCompleted: number;
  staffBreakdown: {
    userId: string;
    userName: string;
    notCompleted: number;
  }[];
}

interface TaskAnalysisResponse {
  data: TaskAnalysisData[];
  summary: {
    totalDays: number;
    totalTasksCreated: number;
    totalNotCompleted: number;
    averageTasksPerDay: number;
  };
}

interface TaskDetail {
  id: string;
  title: string;
  description: string;
  status: string;
  priority: number;
  dueDate: string;
  createdAt: string;
}

interface SelectedStaffTasks {
  userId: string;
  userName: string;
  date: string;
  tasks: TaskDetail[];
}

export default function TaskAnalysisDueDate() {
  const { user } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [isTaskModalOpen, setIsTaskModalOpen] = useState(false);
  const [isLoadingTasks, setIsLoadingTasks] = useState(false);
  const [selectedStaffTasks, setSelectedStaffTasks] =
    useState<SelectedStaffTasks | null>(null);

  // Set default date range for placeholders (last 30 days)
  const today = new Date();
  const thirtyDaysAgo = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);
  const defaultStartDate = thirtyDaysAgo.toISOString().split('T')[0];
  const defaultEndDate = today.toISOString().split('T')[0];

  const {
    data: analysisData,
    isLoading,
    refetch,
  } = useQuery<TaskAnalysisResponse>({
    queryKey: [
      '/api/analytics/task-analysis-due-date',
      startDate || 'all',
      endDate || 'all',
    ],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);

      const qs = params.toString();
      const url = qs
        ? `/api/analytics/task-analysis-due-date?${qs}`
        : '/api/analytics/task-analysis-due-date';
      const response = await apiRequest('GET', url);
      return response.json();
    },
  });

  // Handle errors separately
  useEffect(() => {
    if (analysisData === undefined && !isLoading) {
      // Handle error case
    }
  }, [analysisData, isLoading]);

  const handleDateFilter = () => {
    refetch();
  };

  const handleStaffClick = async (
    userId: string,
    userName: string,
    date: string
  ) => {
    setIsLoadingTasks(true);
    setIsTaskModalOpen(true);

    try {
      const params = new URLSearchParams();
      params.append('userId', userId);
      params.append('date', date);

      const response = await apiRequest(
        'GET',
        `/api/analytics/task-analysis-due-date/staff-tasks?${params.toString()}`
      );
      const data = await response.json();

      setSelectedStaffTasks({
        userId,
        userName,
        date,
        tasks: data.tasks || [],
      });
    } catch (error) {
      console.error('Error fetching staff tasks:', error);
      setSelectedStaffTasks({
        userId,
        userName,
        date,
        tasks: [],
      });
    } finally {
      setIsLoadingTasks(false);
    }
  };

  const exportToCSV = () => {
    if (!analysisData?.data) return;

    const headers = [
      'Date',
      'Total Tasks Due',
      'Not Completed',
      'Staff Member',
      'Staff Not Completed',
    ];
    const rows = [headers];

    analysisData.data.forEach((day: TaskAnalysisData) => {
      if (day.staffBreakdown.length === 0) {
        rows.push([
          day.date,
          day.totalTasks.toString(),
          day.notCompleted.toString(),
          'N/A',
          '0',
        ]);
      } else {
        day.staffBreakdown.forEach(
          (staff: {
            userId: string;
            userName: string;
            notCompleted: number;
          }) => {
            rows.push([
              day.date,
              day.totalTasks.toString(),
              day.notCompleted.toString(),
              staff.userName,
              staff.notCompleted.toString(),
            ]);
          }
        );
      }
    });

    const csvContent = rows.map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `task-analysis-due-date-${startDate || 'all'}-to-${endDate || 'all'}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const exportToPDF = () => {
    if (!analysisData?.data) return;

    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 20;
    let yPosition = margin;

    // Helper function to add a new page if needed
    const checkPageBreak = (requiredHeight: number) => {
      if (yPosition + requiredHeight > pageHeight - margin) {
        doc.addPage();
        yPosition = margin;
      }
    };

    // Title
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.text('Task Analysis Report - Due Date Basis', margin, yPosition);
    yPosition += 15;

    // Date range
    doc.setFontSize(12);
    doc.setFont('helvetica', 'normal');
    let dateRange = 'All time';
    if (startDate && endDate) {
      dateRange = `${formatDate(startDate)} - ${formatDate(endDate)}`;
    } else if (startDate) {
      dateRange = `From ${formatDate(startDate)}`;
    } else if (endDate) {
      dateRange = `Until ${formatDate(endDate)}`;
    }
    doc.text(`Date Range: ${dateRange}`, margin, yPosition);
    yPosition += 10;

    // Summary
    doc.text(
      `Total Days Analyzed: ${analysisData.summary.totalDays}`,
      margin,
      yPosition
    );
    yPosition += 8;
    doc.text(
      `Total Tasks Due: ${analysisData.summary.totalTasksCreated}`,
      margin,
      yPosition
    );
    yPosition += 8;
    doc.text(
      `Total Not Completed: ${analysisData.summary.totalNotCompleted}`,
      margin,
      yPosition
    );
    yPosition += 8;
    doc.text(
      `Average Tasks Per Day: ${analysisData.summary.averageTasksPerDay.toFixed(1)}`,
      margin,
      yPosition
    );
    yPosition += 15;

    // Table headers
    const tableHeaders = [
      'Date',
      'Total Due',
      'Not Completed',
      'Staff Member',
      'Staff Not Completed',
    ];
    const columnWidths = [25, 20, 40, 50, 40];
    const rowHeight = 8;

    // Draw table headers
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    let xPosition = margin;

    tableHeaders.forEach((header, index) => {
      doc.rect(xPosition, yPosition, columnWidths[index], rowHeight);
      doc.text(header, xPosition + 2, yPosition + 5);
      xPosition += columnWidths[index];
    });
    yPosition += rowHeight;

    // Draw table rows
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');

    analysisData.data
      .filter(day => day.notCompleted > 0)
      .forEach((day: TaskAnalysisData) => {
        if (day.staffBreakdown.length === 0) {
          checkPageBreak(rowHeight);
          xPosition = margin;
          const rowData = [
            formatDate(day.date),
            day.totalTasks.toString(),
            day.notCompleted.toString(),
            'N/A',
            '0',
          ];

          rowData.forEach((cell, index) => {
            doc.rect(xPosition, yPosition, columnWidths[index], rowHeight);
            const maxWidth = columnWidths[index] - 4;
            const lines = doc.splitTextToSize(cell, maxWidth);
            doc.text(lines, xPosition + 2, yPosition + 5);
            xPosition += columnWidths[index];
          });
          yPosition += rowHeight;
        } else {
          day.staffBreakdown.forEach(
            (staff: {
              userId: string;
              userName: string;
              notCompleted: number;
            }) => {
              checkPageBreak(rowHeight);
              xPosition = margin;
              const rowData = [
                formatDate(day.date),
                day.totalTasks.toString(),
                day.notCompleted.toString(),
                staff.userName,
                staff.notCompleted.toString(),
              ];

              rowData.forEach((cell, index) => {
                doc.rect(xPosition, yPosition, columnWidths[index], rowHeight);
                const maxWidth = columnWidths[index] - 4;
                const lines = doc.splitTextToSize(cell, maxWidth);
                doc.text(lines, xPosition + 2, yPosition + 5);
                xPosition += columnWidths[index];
              });
              yPosition += rowHeight;
            }
          );
        }
      });

    // Footer
    const totalPages = doc.internal.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setFont('helvetica', 'normal');
      doc.text(
        `Generated on ${new Date().toLocaleDateString()} - Page ${i} of ${totalPages}`,
        margin,
        pageHeight - 10
      );
    }

    // Save the PDF
    const fileName = `task-analysis-due-date-${startDate || 'all'}-to-${endDate || 'all'}.pdf`;
    doc.save(fileName);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'complete':
        return 'bg-green-100 text-green-800';
      case 'pending_approval':
        return 'bg-yellow-100 text-yellow-800';
      case 'dropped':
        return 'bg-red-100 text-red-800';
      case 'working':
        return 'bg-blue-100 text-blue-800';
      case 'need_help':
        return 'bg-orange-100 text-orange-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (!user || !['manager', 'admin'].includes(user.role)) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle className="text-center">Access Denied</CardTitle>
            <CardDescription className="text-center">
              Only managers and admins can view task analysis.
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-gray-50">
      {!sidebarOpen && <Sidebar />}

      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header with Sidebar Toggle */}
        <div className="bg-white border-b border-gray-200 px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              {/* Sidebar Toggle */}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSidebarOpen(!sidebarOpen)}
                className="p-2 h-8 w-8"
                aria-label={sidebarOpen ? 'Show sidebar' : 'Hide sidebar'}
              >
                {sidebarOpen ? (
                  <PanelLeftOpen className="h-4 w-4" />
                ) : (
                  <PanelLeftClose className="h-4 w-4" />
                )}
              </Button>

              <div>
                <h1 className="text-2xl font-bold text-gray-900 flex items-center">
                  <Target className="w-6 h-6 mr-2 text-blue-600" />
                  Task Analysis Report - Due Date Basis
                </h1>
                <p className="text-gray-600 text-sm">
                  Daily task due date analysis with staff-wise breakdown
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                onClick={exportToCSV}
                disabled={!analysisData?.data}
                className="flex items-center gap-2"
              >
                <Download className="w-4 h-4" />
                Export CSV
              </Button>
              <Button
                variant="outline"
                onClick={exportToPDF}
                disabled={!analysisData?.data}
                className="flex items-center gap-2"
              >
                <FileText className="w-4 h-4" />
                Export PDF
              </Button>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="max-w-7xl mx-auto">
            {/* Date Filters */}
            <Card className="mb-6">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Calendar className="h-5 w-5" />
                  Date Range Filter
                </CardTitle>
                <CardDescription>
                  Select the date range for task analysis
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-4">
                  <div className="flex-1">
                    <Label htmlFor="startDate">Start Date</Label>
                    <Input
                      id="startDate"
                      type="date"
                      value={startDate}
                      onChange={e => setStartDate(e.target.value)}
                      className="mt-1"
                      placeholder={defaultStartDate}
                    />
                  </div>
                  <div className="flex-1">
                    <Label htmlFor="endDate">End Date</Label>
                    <Input
                      id="endDate"
                      type="date"
                      value={endDate}
                      onChange={e => setEndDate(e.target.value)}
                      className="mt-1"
                      placeholder={defaultEndDate}
                    />
                  </div>
                  <div className="flex items-end">
                    <Button
                      onClick={handleDateFilter}
                      className="flex items-center gap-2"
                    >
                      <RefreshCw className="w-4 h-4" />
                      Apply Filter
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Summary Cards */}
            {analysisData?.summary && (
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                <Card>
                  <CardContent className="p-4">
                    <div className="text-2xl font-bold text-blue-600">
                      {analysisData.summary.totalDays}
                    </div>
                    <div className="text-sm text-gray-600">Days Analyzed</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <div className="text-2xl font-bold text-green-600">
                      {analysisData.summary.totalTasksCreated}
                    </div>
                    <div className="text-sm text-gray-600">Total Tasks Due</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <div className="text-2xl font-bold text-red-600">
                      {analysisData.summary.totalNotCompleted}
                    </div>
                    <div className="text-sm text-gray-600">Not Completed</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <div className="text-2xl font-bold text-purple-600">
                      {analysisData.summary.averageTasksPerDay.toFixed(1)}
                    </div>
                    <div className="text-sm text-gray-600">Avg Tasks/Day</div>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Analysis Table */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="h-5 w-5" />
                  Daily Task Analysis - Due Date Basis
                </CardTitle>
                <CardDescription>
                  Days with incomplete tasks - breakdown of tasks due and
                  completion status
                </CardDescription>
              </CardHeader>
              <CardContent>
                {isLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <RefreshCw className="h-6 w-6 animate-spin mr-2" />
                    Loading analysis data...
                  </div>
                ) : !analysisData?.data ||
                  analysisData.data.filter(day => day.notCompleted > 0)
                    .length === 0 ? (
                  <div className="text-center py-8">
                    <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">
                      No Incomplete Tasks Found
                    </h3>
                    <p className="text-gray-600">
                      All tasks are completed for the selected date range
                    </p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full border-collapse">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left p-3 font-medium">Date</th>
                          <th className="text-left p-3 font-medium">
                            Total Tasks Due
                          </th>
                          <th className="text-left p-3 font-medium">
                            Not Completed
                          </th>
                          <th className="text-left p-3 font-medium">
                            Staff Breakdown
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {analysisData.data
                          .filter(day => day.notCompleted > 0) // Only show days with incomplete tasks
                          .map((day: TaskAnalysisData, index: number) => (
                            <tr
                              key={index}
                              className="border-b hover:bg-gray-50"
                            >
                              <td className="p-3 font-medium">
                                {new Date(day.date).toLocaleDateString(
                                  'en-GB',
                                  {
                                    day: '2-digit',
                                    month: '2-digit',
                                    year: 'numeric',
                                  }
                                )}
                              </td>
                              <td className="p-3">
                                <Badge
                                  variant="outline"
                                  className="bg-blue-50 text-blue-700"
                                >
                                  {day.totalTasks}
                                </Badge>
                              </td>
                              <td className="p-3">
                                <Badge
                                  variant={
                                    day.notCompleted > 0
                                      ? 'destructive'
                                      : 'default'
                                  }
                                  className={
                                    day.notCompleted === 0
                                      ? 'bg-green-50 text-green-700'
                                      : ''
                                  }
                                >
                                  {day.notCompleted}
                                </Badge>
                              </td>
                              <td className="p-3">
                                <div className="flex flex-wrap gap-1">
                                  {day.staffBreakdown.length === 0 ? (
                                    <span className="text-gray-500 text-sm">
                                      No incomplete tasks
                                    </span>
                                  ) : (
                                    day.staffBreakdown.map(
                                      (
                                        staff: {
                                          userId: string;
                                          userName: string;
                                          notCompleted: number;
                                        },
                                        staffIndex: number
                                      ) => (
                                        <Badge
                                          key={staffIndex}
                                          variant="secondary"
                                          className="text-xs cursor-pointer hover:bg-blue-100 hover:text-blue-800 transition-colors"
                                          onClick={() =>
                                            handleStaffClick(
                                              staff.userId,
                                              staff.userName,
                                              day.date
                                            )
                                          }
                                          title={`Click to view ${staff.userName}'s incomplete tasks`}
                                        >
                                          {staff.userName}: {staff.notCompleted}
                                        </Badge>
                                      )
                                    )
                                  )}
                                </div>
                              </td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Total Summary Card */}
            {analysisData?.data && analysisData.data.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <FileText className="h-5 w-5" />
                    Total Summary
                  </CardTitle>
                  <CardDescription>
                    Overall totals across all dates in the selected range
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {/* Total Tasks Due */}
                    <div className="text-center">
                      <div className="text-3xl font-bold text-blue-600 mb-2">
                        {analysisData.summary.totalTasksCreated}
                      </div>
                      <div className="text-sm text-gray-600">
                        Total Tasks Due
                      </div>
                    </div>

                    {/* Total Not Completed */}
                    <div className="text-center">
                      <div className="text-3xl font-bold text-red-600 mb-2">
                        {analysisData.summary.totalNotCompleted}
                      </div>
                      <div className="text-sm text-gray-600">
                        Total Pending Tasks
                      </div>
                    </div>

                    {/* Completion Rate */}
                    <div className="text-center">
                      <div className="text-3xl font-bold text-green-600 mb-2">
                        {analysisData.summary.totalTasksCreated > 0
                          ? Math.round(
                              ((analysisData.summary.totalTasksCreated -
                                analysisData.summary.totalNotCompleted) /
                                analysisData.summary.totalTasksCreated) *
                                100
                            )
                          : 0}
                        %
                      </div>
                      <div className="text-sm text-gray-600">
                        Completion Rate
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>

      {/* Task Details Modal */}
      <Dialog open={isTaskModalOpen} onOpenChange={setIsTaskModalOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              Task Details - {selectedStaffTasks?.userName} (
              {selectedStaffTasks?.date})
            </DialogTitle>
            <DialogDescription>
              Incomplete tasks due on this date
            </DialogDescription>
          </DialogHeader>

          {isLoadingTasks ? (
            <div className="flex items-center justify-center py-8">
              <RefreshCw className="h-6 w-6 animate-spin mr-2" />
              Loading task details...
            </div>
          ) : selectedStaffTasks?.tasks &&
            selectedStaffTasks.tasks.length > 0 ? (
            <div className="space-y-4">
              {selectedStaffTasks.tasks.map(task => (
                <Card key={task.id}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h4 className="font-semibold text-lg mb-2">
                          {task.title}
                        </h4>
                        {task.description && (
                          <p className="text-gray-600 mb-3">
                            {task.description}
                          </p>
                        )}
                        <div className="flex items-center gap-4 text-sm text-gray-500">
                          <span>Priority: {task.priority}</span>
                          <span>
                            Due: {new Date(task.dueDate).toLocaleDateString()}
                          </span>
                          <span>
                            Created:{' '}
                            {new Date(task.createdAt).toLocaleDateString()}
                          </span>
                        </div>
                      </div>
                      <Badge className={getStatusColor(task.status)}>
                        {task.status.replace('_', ' ')}
                      </Badge>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <div className="text-center text-gray-500 py-8">
              <FileText className="h-12 w-12 mx-auto mb-4 text-gray-300" />
              <p>
                No incomplete tasks found for this staff member on this date.
              </p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
